import { DeletedAttachment, UpsertResult } from "@/reports/types/Api";
import {
  ManualValveReport,
  SafetyValveReport,
  SafetyValveReportWithLocation,
} from "@/reports/types/SafetyValveReport";
import { shortId } from "@/utils/uniqueId";
import { UploadFile } from "antd";

type UpsertOutput = {
  created: SafetyValveReportWithLocation[];
  updated: SafetyValveReportWithLocation[];
};

export const readUpsertResult = (result: UpsertResult[]): UpsertOutput => {
  return result.reduce<UpsertOutput>(
    (prev, current: UpsertResult) => {
      const { location } = current;
      const addLocation = (
        report: SafetyValveReport
      ): SafetyValveReportWithLocation => ({
        ...report,
        location,
      });
      const created = current.created.map(addLocation);
      const updated = current.updated.map(addLocation);
      return {
        created: prev.created.concat(created),
        updated: prev.updated.concat(updated),
      };
    },
    { created: [], updated: [] }
  );
};

/** Reads a single report that was updated/created using the upsert API.
 * Useful when creating or editing a single report.
 * Should not be used for multiple reports.
 */
export const readSingleUpsertResult = (
  result: UpsertResult[]
): SafetyValveReportWithLocation | ManualValveReport => {
  const { created, updated } = readUpsertResult(result);
  const modifications = [...created, ...updated];
  return modifications[0];
};

/**
 * Sanitizes a file name by removing or replacing characters that are not safe for file systems.
 */
function sanitizeFileName(originalName: string): string {
  // Normalize unicode
  let name = originalName.normalize("NFKD");

  // Replace any unicode space with normal space
  name = name.replace(/\s+/gu, " ");

  // Remove non ASCII characters
  // eslint-disable-next-line no-control-regex
  name = name.replace(/[^\x00-\x7F]/g, "");

  // Replace unsafe filename characters
  name = name.replace(/[^a-zA-Z0-9._ -]/g, "");

  // Collapse multiple spaces
  name = name.replace(/ +/g, " ");

  return name.trim();
}

export const generateUniqueFileName = (attachment: UploadFile) => {
  let uniqueFileName = shortId(3);

  // Use a regular expression to match the file name and extension
  const regex = /^(.*[^.])\.(\w+)$/;
  const match = attachment.name.match(regex);

  if (match) {
    const baseName = match[1]; // Get everything before the last dot
    const extension = match[2]; // Get the file extension after the last dot

    uniqueFileName = `${baseName}-${uniqueFileName}.${extension}`;
  } else {
    const fileExtension =
      attachment.type !== undefined
        ? attachment.type.split("/").pop()
        : attachment.name.split(".").pop();
    uniqueFileName = `${uniqueFileName}.${fileExtension}`;
  }
  return uniqueFileName;
};

export const assembleAttachmentFormData = (
  newAddedAttachments: UploadFile[] = [],
  deletedAttachments: DeletedAttachment[] = []
): FormData => {
  const attachmentFormData = new FormData();
  let accumulatedIndex = 0;
  newAddedAttachments.forEach((attachment, index) => {
    const uniqueFileName = sanitizeFileName(generateUniqueFileName(attachment));
    attachmentFormData.append(
      `attachments`,
      attachment.originFileObj as File,
      uniqueFileName
    );
    attachmentFormData.append(
      `Attachments[${index}][FileName]`,
      uniqueFileName
    );
    attachmentFormData.append(`Attachments[${index}][IsNew]`, "true");
    attachmentFormData.append(`Attachments[${index}][IsDeleted]`, "false");
    accumulatedIndex = index + 1;
  });

  // Delete attachments from the server
  deletedAttachments.forEach((attachment, index) => {
    // Non attachment id means it's a new attachment, not from server, no need to delete
    if (attachment.AttachmentId === undefined) return;

    attachmentFormData.append(
      `Attachments[${accumulatedIndex + index}][AttachmentId]`,
      attachment.AttachmentId.toString()
    );
    attachmentFormData.append(
      `Attachments[${accumulatedIndex + index}][IsDeleted]`,
      "true"
    );
    attachmentFormData.append(
      `Attachments[${accumulatedIndex + index}][IsNew]`,
      "false"
    );
  });

  return attachmentFormData;
};
